"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistAnalysisHelper = void 0;
const api_helper_1 = require("./api.helper");
const entry_helper_1 = require("./entry.helper");
const signal_helper_1 = require("./signal.helper");
/**
 * Static helper class for AI Assistant analysis operations
 */
class AIAssistAnalysisHelper {
    /**
     * Analyze current context based on last user message
     */
    static async analyzeCurrentContext(context) {
        var _a;
        if (context.currentContext.length === 0) {
            return {
                thinking: false,
                proposedKnowledgeIds: context.proposedKnowledgeIds,
                minimized: context.minimized
            };
        }
        // Get the last user message from context to analyze
        const lastUserMessage = api_helper_1.AIAssistApiHelper.getLastUserMessage(context.currentContext);
        if (!lastUserMessage || typeof lastUserMessage.content !== 'string') {
            return {
                thinking: false,
                proposedKnowledgeIds: context.proposedKnowledgeIds,
                minimized: context.minimized
            };
        }
        const messageContent = lastUserMessage.content.trim();
        if (!api_helper_1.AIAssistApiHelper.isTextValidForAnalysis(messageContent)) {
            return {
                thinking: false,
                proposedKnowledgeIds: context.proposedKnowledgeIds,
                minimized: context.minimized
            };
        }
        try {
            const request = api_helper_1.AIAssistApiHelper.createAssistRequest(messageContent, context.currentContext, context.proposedKnowledgeIds);
            const responseData = await api_helper_1.AIAssistApiHelper.callAssist(context.apiService, request);
            // Immediately stop thinking state regardless of response content
            if (responseData) {
                if ((_a = responseData.knowledgeResults) === null || _a === void 0 ? void 0 : _a.length) {
                    return await this.handleKnowledgeResults(responseData.knowledgeResults, `${'ai.assist.knowledgeFound'.t()}: ${responseData.knowledgeResults.map(k => k.title).join(', ')}`, context);
                }
            }
        }
        catch (error) {
            // Error analyzing current context
        }
        return {
            thinking: false,
            proposedKnowledgeIds: context.proposedKnowledgeIds,
            minimized: context.minimized
        };
    }
    /**
     * Analyze context after LLM response
     */
    static async analyzeAfterLLMResponse(context) {
        var _a;
        if (context.currentContext.length === 0) {
            return {
                thinking: false,
                proposedKnowledgeIds: context.proposedKnowledgeIds,
                minimized: context.minimized
            };
        }
        // For post-LLM analysis, we want to analyze the full context to find relevant knowledge
        // We'll use the last conversation turn (user + assistant) to understand what knowledge might be relevant
        const conversationText = api_helper_1.AIAssistApiHelper.getRecentConversationText(context.currentContext, 4);
        if (!api_helper_1.AIAssistApiHelper.isTextValidForAnalysis(conversationText)) {
            return {
                thinking: false,
                proposedKnowledgeIds: context.proposedKnowledgeIds,
                minimized: context.minimized
            };
        }
        try {
            // When using conversationText, don't pass context again to avoid duplication
            const request = api_helper_1.AIAssistApiHelper.createAssistRequest(conversationText, [], // Empty context since conversationText already contains the conversation
            context.proposedKnowledgeIds);
            const responseData = await api_helper_1.AIAssistApiHelper.callAssist(context.apiService, request);
            // Immediately stop thinking state when response is received
            if (responseData) {
                // Create entry with analysis, suggestions and knowledge results
                const assistEntry = entry_helper_1.AIAssistEntryHelper.createMessageEntry(responseData.analysis, responseData.suggestions, responseData.knowledgeResults || []);
                if (context.assistantSignal) {
                    signal_helper_1.AIAssistSignalHelper.addEntry(context.assistantSignal, assistEntry);
                }
                // Track new knowledge IDs if we have knowledge results
                let newProposedKnowledgeIds = [...context.proposedKnowledgeIds];
                if ((_a = responseData.knowledgeResults) === null || _a === void 0 ? void 0 : _a.length) {
                    const newKnowledgeIds = responseData.knowledgeResults.map(k => k.id);
                    newProposedKnowledgeIds.push(...newKnowledgeIds);
                }
                return {
                    thinking: false,
                    proposedKnowledgeIds: newProposedKnowledgeIds,
                    minimized: false
                };
            }
        }
        catch (error) {
            // Error analyzing context after LLM response
        }
        return {
            thinking: false,
            proposedKnowledgeIds: context.proposedKnowledgeIds,
            minimized: context.minimized
        };
    }
    /**
     * React to user input with suggestions and knowledge
     */
    static async react(input, context) {
        var _a;
        try {
            const request = api_helper_1.AIAssistApiHelper.createAssistRequest(input, context.currentContext, context.proposedKnowledgeIds);
            const responseData = await api_helper_1.AIAssistApiHelper.callAssist(context.apiService, request);
            // Immediately stop thinking state when response is received
            if (responseData) {
                // Create entry with suggestions and knowledge
                const assistEntry = entry_helper_1.AIAssistEntryHelper.createMessageEntry(responseData.analysis, responseData.suggestions, responseData.knowledgeResults || []);
                if (context.assistantSignal) {
                    signal_helper_1.AIAssistSignalHelper.addEntry(context.assistantSignal, assistEntry);
                }
                // Track new knowledge IDs and show alert if we have knowledge results
                let newProposedKnowledgeIds = [...context.proposedKnowledgeIds];
                if ((_a = responseData.knowledgeResults) === null || _a === void 0 ? void 0 : _a.length) {
                    const newKnowledgeIds = responseData.knowledgeResults.map(k => k.id);
                    newProposedKnowledgeIds.push(...newKnowledgeIds);
                }
                return {
                    thinking: false,
                    proposedKnowledgeIds: newProposedKnowledgeIds,
                    minimized: false
                };
            }
        }
        catch (error) {
            // Error calling AI assist - silently return default state
        }
        return {
            thinking: false,
            proposedKnowledgeIds: context.proposedKnowledgeIds,
            minimized: context.minimized
        };
    }
    /**
     * Handle knowledge results by creating entries and updating state
     */
    static async handleKnowledgeResults(knowledgeResults, contextText, context) {
        // Track proposed knowledge IDs to avoid duplicates
        const newKnowledgeIds = knowledgeResults.map(k => k.id);
        const updatedProposedKnowledgeIds = [...context.proposedKnowledgeIds, ...newKnowledgeIds];
        // Create and add knowledge entry
        const assistEntry = entry_helper_1.AIAssistEntryHelper.createKnowledgeEntry(knowledgeResults, contextText);
        if (context.assistantSignal) {
            signal_helper_1.AIAssistSignalHelper.addEntry(context.assistantSignal, assistEntry);
        }
        return {
            thinking: false,
            proposedKnowledgeIds: updatedProposedKnowledgeIds,
            minimized: false
        };
    }
}
exports.AIAssistAnalysisHelper = AIAssistAnalysisHelper;
